import type { GuideQuestHighlightTarget } from "@yjsgames/guide-quest";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import type { TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { computeBattleTabLayoutInContentRect } from "./battleTabLayout.js";
import type { BottomTabView } from "./BottomTabView.js";
import { TAB_ORDER, type TabId } from "./tabVisibility.js";
import type { GuideQuestPanel } from "./GuideQuestPanel.js";
import type { HudView } from "./HudView.js";

export interface GuideQuestHighlightFrame {
  readonly tabContent: TabContentRect;
  readonly hud: Pick<HudView, "chapterChipRect">;
  readonly bottomTab: Pick<BottomTabView, "tabRect">;
  readonly guideQuestPanel: Pick<GuideQuestPanel, "actionButtonRect">;
  readonly statUpgrade?: { readonly upgradeButtonRect: () => Rect | null };
  readonly shop?: { readonly goldBoxButtonRect: () => Rect | null };
}

export function resolveGuideQuestHighlightRect(
  frame: GuideQuestHighlightFrame,
  target: GuideQuestHighlightTarget,
): Rect | null {
  return resolveGuideQuestHighlightRects(frame, target)[0] ?? null;
}

export function resolveGuideQuestHighlightRects(
  frame: GuideQuestHighlightFrame,
  target: GuideQuestHighlightTarget,
): Rect[] {
  switch (target.kind) {
    case "moduleGrid": {
      const battle = computeBattleTabLayoutInContentRect(frame.tabContent);
      const grid = battle.grid;
      return [
        { x: grid.originX, y: grid.originY, width: grid.size, height: grid.height },
        battle.palette,
      ];
    }
    case "hudChapter":
      return [frame.hud.chapterChipRect];
    case "tab":
      return isTabId(target.tabId) ? compactRect(frame.bottomTab.tabRect(target.tabId)) : [];
    case "statUpgradeAction":
      return compactRect(frame.statUpgrade?.upgradeButtonRect() ?? frame.bottomTab.tabRect("statUpgrade"));
    case "shopAction":
      return compactRect(frame.shop?.goldBoxButtonRect() ?? frame.bottomTab.tabRect("shop"));
    case "guideQuestAction":
      return [frame.guideQuestPanel.actionButtonRect()];
  }
}

export function resolveVisibleGuideQuestHighlightTarget(
  target: GuideQuestHighlightTarget,
  currentTab: string,
): GuideQuestHighlightTarget {
  if (target.kind === "moduleGrid" && currentTab !== "battle") {
    return { kind: "tab", tabId: "battle" };
  }
  if (target.kind === "statUpgradeAction" && currentTab !== "statUpgrade") {
    return { kind: "tab", tabId: "statUpgrade" };
  }
  if (target.kind === "shopAction" && currentTab !== "shop") {
    return { kind: "tab", tabId: "shop" };
  }
  return target;
}

function compactRect(rect: Rect | null): Rect[] {
  return rect ? [rect] : [];
}

function isTabId(value: string): value is TabId {
  return (TAB_ORDER as readonly string[]).includes(value);
}
